import React, { useState, useRef, useEffect } from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, MoreVertical, Search, X, Upload, MessageSquare, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import "@/design-system/typography.css";
import '@/design-system/forms.css';
import { colors } from "@/design-system/colors";

// ============================================================================
// StandardButton - Form Save/Cancel Buttons (Black/White variants)
// ============================================================================

interface StandardButtonProps extends ButtonProps {
  customVariant?: "black" | "white" | "blue";
}

const STANDARD_BUTTON_BASE_CLASSES = "font-button rounded-md h-10 px-4 py-2 text-sm transition-colors";

const STANDARD_BUTTON_VARIANT_CLASSES: Record<NonNullable<StandardButtonProps["customVariant"]>, string> = {
  black: "text-white border border-transparent",
  white: "text-black border border-gray-200",
  blue: "text-white border border-transparent",
};

// Single source of truth for a variant's resting/hover background --
// consumed by every Save/Cancel button in the app via StandardButton
// (and SmallStandardButton below) instead of each modal hand-rolling its
// own inline style + onMouseEnter/onMouseLeave pair.
function standardButtonColors(customVariant: StandardButtonProps["customVariant"]) {
  switch (customVariant) {
    case "black":
      return { bg: colors.buttonPrimary, hoverBg: colors.buttonPrimaryHover, style: { backgroundColor: colors.buttonPrimary, color: colors.buttonTextWhite } };
    case "white":
      return { bg: colors.buttonTertiary, hoverBg: colors.buttonTertiaryHover, style: { backgroundColor: colors.buttonTertiary } };
    case "blue":
      return { bg: colors.buttonSecondary, hoverBg: colors.buttonSecondaryHover, style: { backgroundColor: colors.buttonSecondary, color: colors.buttonTextWhite, border: 'none' } };
    default:
      return null;
  }
}

export function StandardButton({
  className,
  variant = "default",
  customVariant,
  disabled,
  style,
  onMouseEnter,
  onMouseLeave,
  ...props
}: StandardButtonProps) {
  const appliedClass = customVariant
    ? cn(STANDARD_BUTTON_BASE_CLASSES, STANDARD_BUTTON_VARIANT_CLASSES[customVariant], className)
    : cn("font-button", className);

  const variantColors = customVariant ? standardButtonColors(customVariant) : null;

  // A caller's own `style` (a smaller padding, say) is ADDED to the variant's colours - it used to replace them (the props were spread after `style`), so such a
  // button lost its background and showed the base dark one until the mouse passed over it. Its own hover handlers run after the variant's.
  return (
    <Button
      className={appliedClass}
      variant={customVariant ? "default" : variant}
      style={variantColors || style ? { ...variantColors?.style, ...style } : undefined}
      disabled={disabled}
      onMouseEnter={(e) => {
        if (variantColors && !disabled) {
          e.currentTarget.style.backgroundColor = variantColors.hoverBg;
        }
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        if (variantColors && !disabled) {
          e.currentTarget.style.backgroundColor = variantColors.bg;
        }
        onMouseLeave?.(e);
      }}
      {...props}
    />
  );
}

// ============================================================================
// SaveChangesButton / CancelButton - the two GenericForm footer buttons,
// as importable standalone components. Every modal's Save/Cancel should
// use these instead of reaching for StandardButton + a customVariant
// directly, so sizing/color/hover for the pair can never drift apart
// between modals -- change it once here.
// ============================================================================

export function SaveChangesButton({ children, type = "submit", ...props }: Omit<StandardButtonProps, "customVariant">) {
  return (
    <StandardButton customVariant="blue" type={type} {...props}>
      {children ?? "Save Changes"}
    </StandardButton>
  );
}

export function CancelButton({ children, type = "button", ...props }: Omit<StandardButtonProps, "customVariant">) {
  return (
    <StandardButton customVariant="white" type={type} {...props}>
      {children ?? "Cancel"}
    </StandardButton>
  );
}

// ============================================================================
// PrimaryIconButton / TertiaryIconButton - bare, textless square buttons
// (h-10 w-10) in the primary/tertiary colors, for wrapping a single icon
// wherever a Save/Cancel-style button with no label is needed (mirrors
// what ThreeDotMenuButton/SearchButton below already build by hand).
// ============================================================================

interface IconOnlyButtonProps extends Omit<ButtonProps, "children"> {
  children: React.ReactNode;
}

export function PrimaryIconButton({ className, ...props }: IconOnlyButtonProps) {
  return (
    <Button
      className={cn("rounded-md h-10 w-10 p-0 transition-colors border border-transparent", className)}
      style={{ backgroundColor: colors.buttonPrimary, color: colors.buttonTextWhite }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimaryHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.buttonPrimary; }}
      {...props}
    />
  );
}

export function TertiaryIconButton({ className, ...props }: IconOnlyButtonProps) {
  return (
    <Button
      variant="outline"
      className={cn("border border-gray-300 rounded-md h-10 w-10 p-0 transition-colors focus-visible:ring-0 focus-visible:ring-offset-0", className)}
      style={{ backgroundColor: colors.buttonTertiary }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.buttonTertiary; }}
      {...props}
    />
  );
}

// ============================================================================
// NewItemButton - Black button with Plus icon for creating new items
// ============================================================================

interface NewItemButtonProps extends Omit<ButtonProps, 'children'> {
  label: string;
}

export function NewItemButton({ 
  label,
  className,
  ...props 
}: NewItemButtonProps) {
  return (
    <Button
      className={cn(
        "text-white rounded-md font-button h-10 px-4 py-2 text-sm transition-colors",
        className
      )}
      style={{ backgroundColor: colors.buttonPrimary }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = colors.buttonPrimaryHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = colors.buttonPrimary;
      }}
      {...props}
    >
      <Plus className="h-4 w-4 mr-2" />
      {label}
    </Button>
  );
}

// ============================================================================
// SecondaryButton - Blue-gray button for secondary actions
// ============================================================================

interface SecondaryButtonProps extends ButtonProps {
  children: React.ReactNode;
}

// Was its own hand-rolled <Button variant="outline" size="sm"> -- shadcn's
// "sm" size is h-9/px-3, a full 4px shorter and 4px narrower than
// StandardButton's h-10/px-4, so every "secondary blue" button built this
// way (New Task, Send to Vendor, Save Changes in a few older forms, etc.)
// silently rendered smaller than Cancel/Save Changes sitting right next to
// it. Delegating to StandardButton's own "blue" variant means the two can
// never drift apart again -- one size/color definition, not two.
export function SecondaryButton({ children, ...props }: SecondaryButtonProps) {
  return (
    <StandardButton customVariant="blue" {...props}>
      {children}
    </StandardButton>
  );
}

// ============================================================================
// ThreeDotMenuButton - White button with dropdown menu
// ============================================================================

export interface ThreeDotMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

interface ThreeDotMenuButtonProps {
  items: ThreeDotMenuItem[];
  className?: string;
}

export function ThreeDotMenuButton({ items, className }: ThreeDotMenuButtonProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="More actions"
          variant="outline"
          size="default"
          className={cn(
            "border border-gray-300 w-10 h-10 p-0 rounded-md transition-colors focus-visible:ring-0 focus-visible:ring-offset-0",
            className
          )}
          style={{ backgroundColor: colors.buttonTertiary }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiary;
          }}
        >
          <MoreVertical className="w-4 h-4 text-black" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
        {items.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onClick={item.disabled ? undefined : item.onClick}
            disabled={item.disabled}
            className={cn(
              "cursor-pointer focus:bg-gray-100 data-[highlighted]:bg-gray-100 font-button",
              item.destructive && "text-red-600 focus:text-red-700",
              item.disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            {item.icon && <span className="w-4 h-4 mr-2">{item.icon}</span>}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ============================================================================
// SmallThreeDotMenuButton - Smaller version of ThreeDotMenuButton (3/4 size)
// ============================================================================

interface SmallThreeDotMenuButtonProps {
  items: ThreeDotMenuItem[];
  className?: string;
}

export function SmallThreeDotMenuButton({ items, className }: SmallThreeDotMenuButtonProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="default"
          className={cn(
            "border-0 w-7 h-7 p-0 rounded-md transition-colors focus-visible:ring-0 focus-visible:ring-offset-0",
            className
          )}
          style={{
            backgroundColor: colors.buttonTertiary,
            color: 'rgb(var(--primary))'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiary;
          }}
        >
          <MoreVertical className="w-3 h-3" style={{ color: 'rgb(var(--primary))' }} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
        {items.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onClick={item.disabled ? undefined : item.onClick}
            disabled={item.disabled}
            className={cn(
              "cursor-pointer focus:bg-gray-100 data-[highlighted]:bg-gray-100 font-button",
              item.destructive && "text-red-600 focus:text-red-700",
              item.disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            {item.icon && <span className="w-3.5 h-3.5 mr-2">{item.icon}</span>}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ============================================================================
// SearchButton - White button with search icon that opens input field
// ============================================================================

interface SearchButtonProps {
  onSearch: (query: string) => void;
  placeholder?: string;
  className?: string;
}

export function SearchButton({ 
  onSearch, 
  placeholder = "Search...",
  className 
}: SearchButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const onSearchRef = useRef(onSearch);

  // Keep the ref updated
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Clear search when component unmounts
  useEffect(() => {
    return () => {
      onSearchRef.current("");
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    onSearch(value);
  };

  const handleClear = () => {
    setSearchQuery("");
    onSearch("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleClose = () => {
    setSearchQuery("");
    onSearch("");
    setIsOpen(false);
  };

  // Handle ESC key to close search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setSearchQuery("");
        onSearch("");
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, onSearch]);

  return (
    <div className={cn("relative", className)}>
      {!isOpen ? (
        <Button
          variant="outline"
          size="default"
          onClick={() => setIsOpen(true)}
          className="border border-gray-300 w-10 h-10 p-0 rounded-md transition-colors focus-visible:ring-0 focus-visible:ring-offset-0"
          style={{ backgroundColor: colors.buttonTertiary }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.buttonTertiary;
          }}
        >
          <Search className="w-4 h-4 text-gray-600" />
        </Button>
      ) : (
        <div className="relative flex items-center">
          <Input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={placeholder}
            className="pl-3 pr-9 h-10 w-64 font-form-input bg-white border-gray-300 focus:border-gray-400"
          />
          <button
            onClick={handleClose}
            className="absolute right-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            title="Close search"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// UploadFilesButton - White button with Upload icon for file uploads
// ============================================================================

interface UploadFilesButtonProps extends Omit<ButtonProps, 'children' | 'onClick'> {
  onUpload: (files: FileList | null) => void;
  accept?: string;
  multiple?: boolean;
  id?: string;
  className?: string;
}

export function UploadFilesButton({ 
  onUpload,
  accept,
  multiple = true,
  id = "upload-files-button",
  className,
  ...props 
}: UploadFilesButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpload(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        className="hidden"
        id={id}
        accept={accept}
        multiple={multiple}
      />
      <Button
        type="button"
        onClick={handleClick}
        className={cn(
          "text-white rounded-md font-button h-10 px-4 py-2 transition-colors",
          className
        )}
        style={{ backgroundColor: colors.buttonSecondary }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonSecondaryHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonSecondary;
        }}
        {...props}
      >
        <Upload className="h-4 w-4 mr-2" />
        Upload Files
      </Button>
    </>
  );
}

// ============================================================================
// AddCommentButton - Black button with MessageSquare icon for adding comments
// ============================================================================

interface AddCommentButtonProps extends ButtonProps {
  disabled?: boolean;
}

export function AddCommentButton({ 
  className,
  disabled,
  ...props 
}: AddCommentButtonProps) {
  return (
    <Button
      type="button"
      disabled={disabled}
      className={cn(
        "text-white rounded-md font-button h-10 px-4 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
      style={{ backgroundColor: colors.buttonSecondary }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = colors.buttonSecondaryHover;
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = colors.buttonSecondary;
        }
      }}
      {...props}
    >
      Add Comment
    </Button>
  );
}

// ============================================================================
// CloseButton - Standardized X button for form headers
// ============================================================================

interface CloseButtonProps extends Omit<ButtonProps, 'children'> {
  onClick: () => void;
}

export function CloseButton({ 
  onClick,
  className,
  ...props 
}: CloseButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "generic-close-button",
        className
      )}
      {...props}
    >
      <X className="generic-close-button-icon" />
    </button>
  );
}

// ============================================================================
// SmallStandardButton - Small form Save/Cancel Buttons (Black/White variants)
// ============================================================================

interface SmallStandardButtonProps extends ButtonProps {
  customVariant?: "black" | "white" | "blue";
}

export function SmallStandardButton({ 
  className, 
  variant = "default", 
  customVariant,
  ...props 
}: SmallStandardButtonProps) {
  const baseClasses = "rounded-md h-auto px-3 py-1 font-small-button transition-colors";
  
  const customStyles = {
    black: "text-white border border-transparent",
    white: "text-black border border-gray-200",
    blue: "text-white border border-transparent",
  };

  const appliedClass = customVariant
    ? cn(baseClasses, customStyles[customVariant], className)
    : cn("font-small-button", className);

  const buttonStyle = customVariant === "black"
    ? { backgroundColor: colors.buttonPrimary, color: colors.buttonTextWhite }
    : customVariant === "white"
    ? { backgroundColor: colors.buttonTertiary }
    : customVariant === "blue"
    ? { backgroundColor: colors.buttonSecondary, color: colors.buttonTextWhite }
    : undefined;

  return (
    <Button
      className={appliedClass}
      variant={customVariant ? "default" : variant}
      style={buttonStyle}
      onMouseEnter={(e) => {
        if (customVariant === "black") {
          e.currentTarget.style.backgroundColor = colors.buttonPrimaryHover;
        } else if (customVariant === "white") {
          e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover;
        } else if (customVariant === "blue") {
          e.currentTarget.style.backgroundColor = colors.buttonSecondaryHover;
        }
      }}
      onMouseLeave={(e) => {
        if (customVariant === "black") {
          e.currentTarget.style.backgroundColor = colors.buttonPrimary;
        } else if (customVariant === "white") {
          e.currentTarget.style.backgroundColor = colors.buttonTertiary;
        } else if (customVariant === "blue") {
          e.currentTarget.style.backgroundColor = colors.buttonSecondary;
        }
      }}
      {...props}
    />
  );
}

// ============================================================================
// SmallSecondaryButton - Small blue-gray button for secondary actions
// ============================================================================

interface SmallSecondaryButtonProps extends ButtonProps {
  children: React.ReactNode;
}

export function SmallSecondaryButton({ 
  children,
  className,
  ...props 
}: SmallSecondaryButtonProps) {
  return (
    <Button
      variant="outline"
      className={cn(
        "rounded-md h-auto px-3 py-1 font-small-button-white transition-colors",
        className
      )}
      style={{ 
        backgroundColor: colors.buttonSecondary,
        color: colors.buttonTextWhite,
        border: 'none'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = colors.buttonSecondaryHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = colors.buttonSecondary;
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

// DeleteButton - Small icon button for deleting items (rows, columns, etc.)
// ============================================================================

/**
 * The hover of every white / tertiary button that is built by hand (not through StandardButton): the same
 * `colors.buttonTertiaryHover` StandardButton's "white" variant uses, never a Tailwind gray. Spread it on the element; the resting
 * background stays whatever the element's own class says (the leave handler clears the inline color).
 */
export const tertiaryHover = {
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover; },
  onMouseLeave: (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.backgroundColor = ''; },
};

/**
 * The labeled "Delete <record>" button of a detail drawer / panel (Delete Task, Delete Phase...): trash icon + label, red text on a
 * white bordered button. One look for every record's delete action; the caller supplies the label, the layout (className) and
 * -- as everywhere in the app -- asks `window.confirm` before deleting.
 */
export function DeleteRecordButton({ label, className, ...props }: { label: string } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        "flex items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 transition-colors hover:text-red-700",
        className
      )}
      {...tertiaryHover}
      {...props}
    >
      <Trash2 className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}

interface DeleteButtonProps extends ButtonProps {
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export function DeleteButton({ 
  onClick,
  disabled,
  className,
  ...props 
}: DeleteButtonProps) {
  // Hover uses the same token as every other white/tertiary icon button (colors.buttonTertiaryHover), set the same
  // way ThreeDotMenuButton / SearchButton do it -- not a one-off Tailwind gray.
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "h-6 w-6 p-0 rounded-md transition-colors hover:bg-transparent",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.backgroundColor = colors.buttonTertiaryHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '';
      }}
      {...props}
    >
      <Trash2 className="h-3 w-3" />
    </Button>
  );
}
